import API from "helpers/axios";
import { GovGatewayStatus } from "components/v2/templates/instant-vat/dashboard/GovernmentGatewayPanel";
import { VatReturnsAndRefund } from "components/v2/templates/instant-vat/dashboard/VatReturnsAndRefunds";

export interface GetBankingDetailsResponse {
    sort_code: string;
    account_number: string;
}

export interface UpdateBankingDetailsRequest {
    company_id: string;
    sort_code: string;
    account_number: string;
}

export interface UpdateUserAccountRequest {
    company_number: string;
    partner_name?: string;
    company_id?: string;
}

export interface GetPricingResponse {
    adsum_fee: number;
    broker_fee: number;
    trial_months: number;
    trial_days: number;
    discount_percent: number;
    monthly_fees: number;
    subscription_plan: boolean;
}

export interface SubmitGovGateway2FaChoiceRequest {
    company_id: string;
    option: string;
}

export interface SubmitGovGatewayOTPRequest {
    company_id: string;
    code: string;
}

export interface SubmitGovGatewayLoginRequest {
    company_id: string;
    user_id: string;
    password: string;
}

export interface GetGovGatewayStatusResponse {
    status: GovGatewayStatus;
}

export interface GetEligibilityInfoResponse {
    check: string;
    status: "PENDING" | "SUCCESS" | "ERROR" | "WARNING";
}

export interface SendEligibilityPopupAlertRequest {
    company_id: string;
    company_eligibility: string;
}

export interface SendVatReturnDetailsAlertRequest {
    company_id: string;
    status: string;
    period: string;
}

export interface GetFeedbackStatusResponse {
    show_feedback_popup: boolean;
}

export interface GetVatReturnAndRefundListResponse {
    data: VatReturnsAndRefund[];
    total_count: number;
}

export interface SubmitUserSourceFeedbackRequest {
    company_id: string;
    source: string;
}

export interface GetCompanyInUseRequest {
    company_id: string;
    email?: string;
    full_name?: string;
}

export interface SendInstantVatConsentRequest {
    company_id: string;
    consent: boolean;
}

export interface GetRepaymentBankDetailsResponse {
    show_repayment_bank: boolean;
    status: "pending" | "complete";
    sort_code?: string;
    account_number?: string;
}

export interface GetBankingConnectionStatusResponse {
    linked: boolean;
}

export interface BankSearchOption {
    platform_key: string;
    display_name: string;
    logo_url: string;
    provider: string;
}

export interface BankAccountLinkRequest {
    company_id: string;
    platform_key: string;
    display_name: string;
    provider: string;
}

export interface BookkeepingLinkRequest {
    company_id: string;
    platform_key: string;
    display_name: string;
}

export interface BookkeepingLinkResponse {
    redirect_url: string;
}

export interface BankAccountLinkResponse {
    redirect_url: string;
}

export type BookkeepingSearchOption = Omit<BankSearchOption, "provider">;

export interface GetSetupStatusResponse {
    completed: boolean;
}

export interface GovernmentGatewayAccessDetailsResponse {
    show_gg_access_status: boolean;
    status: "locked" | "unlocked";
}

export interface AddDirectorRequest {
    company_id: string;
    email: string;
    full_name: string;
    phone_number: string;
}

export interface instantVATStatusData {
    status: boolean;
}

export const InstantVatAPI = {
    getBankingDetails(companyId: string) {
        return API.get<GetBankingDetailsResponse>("/mtd-partner/banking-details", {
            params: {
                company_id: companyId,
            },
        });
    },
    updateBankingDetails(data: UpdateBankingDetailsRequest) {
        return API.post("/mtd-partner/banking-details", data);
    },
    updateUserAccount(data: UpdateUserAccountRequest) {
        return API.post("/mtd-partner/user-account", data);
    },
    getPricing(companyId: string) {
        return API.get<GetPricingResponse>("/mtd-partner/pricing", {
            params: {
                company_id: companyId,
            },
        });
    },
    tcScrollToBottom(companyId: string) {
        return API.post("/mtd-partner/tc-scroll-to-bottom", {
            company_id: companyId,
        });
    },
    tcReadingTime(companyId: string, timeInSeconds = 10) {
        return API.post("/mtd-partner/tc-reading-time", {
            time: timeInSeconds,
            company_id: companyId,
        });
    },
    preExistentGovGateway(companyId: string) {
        return API.post("/mtd-partner/pre-existent-gov-gateway", {
            company_id: companyId,
        });
    },
    submitGovGateway2FaChoice(data: SubmitGovGateway2FaChoiceRequest) {
        return API.post("/gov-gateway-2fa-choice", data);
    },
    submitGovGatewayOTP(data: SubmitGovGatewayOTPRequest) {
        return API.post("/gov-gateway-opt", data);
    },
    skipGovGateway(companyId: string, reason: string) {
        return API.post("/skip-gov-gateway", {
            company_id: companyId,
            reason: reason,
        });
    },
    submitGovGatewayLogin(data: SubmitGovGatewayLoginRequest) {
        return API.post("/gov-gateway-login", data);
    },
    getGovGatewayStatus(companyId: string) {
        return API.get<GetGovGatewayStatusResponse>("/mtd-partner/gov-gateway", {
            params: {
                company_id: companyId,
            },
        });
    },
    getEligibilityInfo(companyId: string) {
        return API.get<GetEligibilityInfoResponse>("/mtd-partner/eligibility-check", {
            params: {
                company_id: companyId,
            },
        });
    },
    sendEligibilityPopupAlert(data: SendEligibilityPopupAlertRequest) {
        return API.post("/mtd-partner/eligibility-popup-alert", data);
    },
    sendVatReturnDetailsAlert(data: SendVatReturnDetailsAlertRequest) {
        return API.post("/mtd-partner/vat-return-details-alert", data);
    },
    getVatReturnAndRefundList(companyId: string) {
        return API.get<GetVatReturnAndRefundListResponse>("/mtd-partner/vat-return-and-refund-list", {
            params: {
                company_id: companyId,
            },
        });
    },
    getInstantVatStatus(companyId: string) {
        return API.get<instantVATStatusData>("/mtd-partner/instant-vat-status", {
            params: {
                company_id: companyId,
            },
        });
    },
    submitUserSourceFeedback(data: SubmitUserSourceFeedbackRequest) {
        return API.post<GetFeedbackStatusResponse>("/mtd-partner/deal-source-alert", data);
    },
    getCompanyInUse(company_name: string) {
        return API.get<string>("/company-in-use", {
            params: {
                company_name: company_name,
            },
        });
    },
    sendAuthorisationRequest(data: GetCompanyInUseRequest) {
        return API.post<boolean>("/company-in-use/authorisation-request", data);
    },
    sendInstantVatConsent(data: SendInstantVatConsentRequest) {
        return API.post<boolean>("/instant-vat/consent", data);
    },
    getRepaymentBankDetails(companyId: string) {
        return API.get<GetRepaymentBankDetailsResponse>("/repayment-bank-details", {
            params: {
                company_id: companyId,
            },
        });
    },
    getBankingConnectionStatus(companyId: string) {
        return API.get<GetBankingConnectionStatusResponse>("/banking/connection", {
            params: {
                company_id: companyId,
            },
        });
    },
    getBankOptions(companyId: string) {
        return API.get<BankSearchOption[]>("/banking/providers", {
            params: {
                company_id: companyId,
            },
        });
    },
    linkBankAccount(data: BankAccountLinkRequest) {
        return API.post<BankAccountLinkResponse>("/banking/connection", data);
    },
    getBookkeepingSoftwareOptions(companyId: string) {
        return API.get<BookkeepingSearchOption[]>("/accounting/providers", {
            params: {
                company_id: companyId,
            },
        });
    },
    linkBookkeepingSoftware(data: BookkeepingLinkRequest) {
        return API.post<BookkeepingLinkResponse>("/accounting/connection", data);
    },
    getBookkeepingSoftwareStatus(companyId: string) {
        return API.get("/accounting/connection", {
            params: {
                company_id: companyId,
            },
        });
    },
    getSetupStatus(companyId: string) {
        return API.get<GetSetupStatusResponse>("/setup/status", {
            params: {
                company_id: companyId,
            },
        });
    },
    sendHelpPageAlert(companyId: string, activity: string) {
        return API.post("/instant-vat/help-page-alert", {
            company_id: companyId,
            activity: activity,
        });
    },

    checkFundedUser(companyId: string) {
        return API.get<boolean>("/instant-vat/funded-company", {
            params: {
                company_id: companyId,
            },
        });
    },
    fetchReturnSubmissionDate(companyId: string, period_key: string) {
        return API.get("/vat-return-submission-date", {
            params: {
                company_id: companyId,
                period_key: period_key,
            },
        });
    },
    fetchGGCompanyAddress(companyId: string) {
        return API.get("/gg-company-address", {
            params: {
                company_id: companyId,
            },
        });
    },
    getFundedRefunds(companyId: string) {
        return API.get("/instant-vat/funded-refunds", {
            params: {
                company_id: companyId,
            },
        });
    },
    getDaysSaved(companyId: string) {
        return API.get("/instant-vat/days-saved", {
            params: {
                company_id: companyId,
            },
        });
    },
    getInvoices(companyId: string) {
        return API.get("/instant-vat/invoices", {
            params: {
                company_id: companyId,
            },
        });
    },
    downloadInvoice(companyId: string, invoiceNumber: string) {
        return API.get("/mtd-partner/download-invoice", {
            params: {
                company_id: companyId,
                invoice_number: invoiceNumber,
            },
        });
    },

    setPricingPlan(company_id: string, plan: string) {
        return API.post("/mtd-partner/pricing", {
            company_id: company_id,
            plan: plan,
        });
    },
};
